import { useEffect, useState, type ReactNode } from 'react';
import {
  useGetGeoObjectsQuery,
  useGetKfoLineQuery,
  usePatchKfoLineMutation,
  type KfoLine,
} from '@shared/api';
import { Button } from '@shared/ui/Button';
import { Input, Select } from '@shared/ui/Input';
import {
  Actions,
  Banner,
  CONTROL_KIND_LABEL,
  DataTable,
  Drawer,
  Field,
  KvGrid,
  Muted,
  PanelTitle,
  REPORT_KIND_LABEL,
  RFI_STATUS_LABEL,
  SOURCE_LABEL,
  TableWrap,
  Tag,
  errorText,
  fmtDate,
  fmtDateTime,
  fmtNum,
  guaranteeClosedText,
  isGuaranteeOverdue,
} from './ui';

/** Подписи полей учёта Заказчика в журнале изменений между срезами. */
const FIELD_LABEL: Record<string, string> = {
  fo_per_element: 'ФО на элемент',
  element_count: 'Кол-во элементов',
  fo_total: 'Всего ФО по РД',
  fact_cum: 'Факт СМР накопительно',
  fact_km: 'Факт КМ',
  last_fact_date: 'Дата последнего факта',
  accepted_gk: 'Принято ГК',
  accepted_sk: 'Принято СК',
  id_presented: 'ИД предъявлено',
  id_on_signing: 'ИД на подписании',
  id_accepted: 'ИД принято',
  stdo: 'СТДО',
  pa_accepted: 'Принято ПА',
  nzp: 'НЗП',
  dk_volume: 'Объём ДК',
  last_rfi_no: 'Последний RFI',
  ep_code: 'Шифр ЕР',
  rd_revision: 'Ревизия',
};

const fieldLabel = (f: string) => FIELD_LABEL[f] ?? f;

const val = (v: number | string | null | undefined) =>
  v === null || v === undefined || v === '' ? '—' : typeof v === 'number' ? fmtNum(v) : v;

function Kv({ items }: { items: [string, ReactNode][] }) {
  const shown = items.filter(([, v]) => v !== null && v !== undefined && v !== '');
  if (shown.length === 0) return null;
  return (
    <KvGrid>
      {shown.map(([k, v]) => (
        <div key={k} style={{ display: 'contents' }}>
          <dt>{k}</dt>
          <dd>{v}</dd>
        </div>
      ))}
    </KvGrid>
  );
}

/**
 * Карточка строки КФО: паспорт, учёт Заказчика по последнему срезу, наши
 * отчёты, RFI, гарантийные обязательства и изменения чисел между срезами.
 */
export function KfoLineDrawer({
  projectId,
  lineId,
  onClose,
  onOpenReport,
}: {
  projectId: string;
  lineId: string | null;
  onClose: () => void;
  onOpenReport: (reportId: string) => void;
}) {
  const { data, isLoading, error } = useGetKfoLineQuery(
    { projectId, lineId: lineId ?? '' },
    { skip: !lineId },
  );
  const line: KfoLine | undefined = data?.line;
  const [patchLine, { isLoading: saving }] = usePatchKfoLineMutation();
  const { data: geoObjects } = useGetGeoObjectsQuery(undefined, { skip: !lineId });
  const projectElements = (geoObjects?.elements ?? []).filter(
    (e) => e.project_id === projectId && e.position_id,
  );

  const [posId, setPosId] = useState('');
  const [note, setNote] = useState('');
  const [saveErr, setSaveErr] = useState<string | null>(null);

  useEffect(() => {
    setPosId(line?.position_id ?? '');
    setNote(line?.note ?? '');
    setSaveErr(null);
  }, [line?.id, line?.position_id, line?.note]);

  async function onSaveBinding() {
    if (!line) return;
    setSaveErr(null);
    const body: {
      projectId: string;
      lineId: string;
      position_id?: string | null;
      note?: string | null;
    } = { projectId, lineId: line.id };
    const cleanPos = posId.trim() || null;
    const cleanNote = note.trim() || null;
    if ((line.position_id ?? null) !== cleanPos) body.position_id = cleanPos;
    if ((line.note ?? null) !== cleanNote) body.note = cleanNote;
    if (body.position_id === undefined && body.note === undefined) return;
    try {
      await patchLine(body).unwrap();
    } catch (e) {
      setSaveErr(errorText(e, 'Не удалось сохранить привязку строки КФО.'));
    }
  }

  return (
    <Drawer
      open={!!lineId}
      onClose={onClose}
      title={
        line ? (
          <>
            Ключ {line.ext_key}{' '}
            {line.absent && (
              <Tag $tone="warning" title="Строки нет в последнем срезе Заказчика">
                нет в срезе
              </Tag>
            )}
          </>
        ) : (
          'Строка КФО'
        )
      }
    >
      {isLoading && <Muted>Загрузка…</Muted>}
      {error && <Banner $tone="danger">{errorText(error, 'Не удалось открыть строку.')}</Banner>}
      {line && data && (
        <>
          <PanelTitle>Паспорт строки</PanelTitle>
          <Kv
            items={[
              ['№ договора', line.contract_no],
              ['Шифр листа РД', line.rd_sheet_code],
              [
                'Ревизия',
                line.rd_revision
                  ? `${line.rd_revision}${line.revision_date ? ` от ${fmtDate(line.revision_date)}` : ''}`
                  : null,
              ],
              ['Наименование документа', line.document_name],
              ['Основание', line.basis],
              ['Зона', line.zone],
              ['Титул', line.titul],
              ['Марка', line.mark],
              ['Вид работ', line.work_kind],
              ['Источник ЕР', line.ep_source],
              ['Шифр ЕР', line.ep_code],
              ['Наименование ЕР', line.ep_name],
              ['Элемент', line.element],
              ['Ед. изм.', line.unit],
              [
                'ФО на элемент × кол-во',
                line.fo_per_element !== null || line.element_count !== null
                  ? `${val(line.fo_per_element)} × ${val(line.element_count)}`
                  : null,
              ],
              ['Всего ФО по РД', fmtNum(line.fo_total)],
              ['Ответственный от ОС Заказчика', line.customer_responsible],
              ['Подрядчик', line.contractor],
              ['Позиция РД (position_id)', line.position_id],
              ['Наше примечание', line.note],
            ]}
          />

          <PanelTitle>Привязка к позиции РД и примечание ПТО</PanelTitle>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
            {projectElements.length > 0 && (
              <Field>
                Выбрать из конструктива площадки (позиция РД)
                <Select value={posId} onChange={(e) => setPosId(e.target.value)}>
                  <option value="">— не привязана —</option>
                  {projectElements.map((el) => (
                    <option key={el.id} value={el.position_id ?? ''}>
                      {el.code ? `${el.code} · ` : ''}
                      {el.title}
                      {el.axes ? ` (оси ${el.axes})` : ''}
                    </option>
                  ))}
                </Select>
              </Field>
            )}
            <Field>
              UUID позиции объёма РД (position_id)
              <Input
                value={posId}
                onChange={(e) => setPosId(e.target.value)}
                placeholder="UUID позиции РД (пусто — отвязать)"
              />
            </Field>
            <Field>
              Наше примечание
              <Input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Примечание инженера ПТО…"
              />
            </Field>
            {saveErr && <Banner $tone="danger">{saveErr}</Banner>}
            <Actions>
              <Button
                $size="sm"
                type="button"
                disabled={
                  saving ||
                  ((line.position_id ?? '') === posId.trim() && (line.note ?? '') === note.trim())
                }
                onClick={() => void onSaveBinding()}
              >
                {saving ? 'Сохраняю…' : 'Сохранить привязку'}
              </Button>
            </Actions>
          </div>

          <PanelTitle>Учёт Заказчика (последний срез)</PanelTitle>
          <Kv
            items={[
              ['Факт СМР накопительно', fmtNum(line.fact_cum)],
              ['Факт КМ', fmtNum(line.fact_km)],
              ['Дата учтённого факта СМР', fmtDate(line.last_fact_date)],
              ['Принято СК', fmtNum(line.accepted_sk)],
              ['Принято ГК', fmtNum(line.accepted_gk)],
              ['ИД предъявлено', fmtNum(line.id_presented)],
              ['ИД на подписании', fmtNum(line.id_on_signing)],
              ['ИД принято', fmtNum(line.id_accepted)],
              ['СТДО', fmtNum(line.stdo)],
              ['Принято ПА', fmtNum(line.pa_accepted)],
              ['НЗП', fmtNum(line.nzp)],
              ['Объём ДК', fmtNum(line.dk_volume)],
              ['Последний № RFI', line.last_rfi_no !== null ? String(line.last_rfi_no) : null],
              [
                'KQ-1 / Коэф-1',
                line.kq1 || line.coef1 !== null ? `${line.kq1 ?? '—'} / ${val(line.coef1)}` : null,
              ],
              [
                'KQ-2 / Коэф-2',
                line.kq2 || line.coef2 !== null ? `${line.kq2 ?? '—'} / ${val(line.coef2)}` : null,
              ],
              ['Самовольно', fmtNum(line.unauthorized)],
              ['Примечание Заказчика', line.customer_note],
            ]}
          />
          {Object.keys(line.stages ?? {}).length > 0 && (
            <>
              <Muted style={{ marginBottom: 6 }}>Стадии испытаний и приёмки</Muted>
              <Kv
                items={Object.entries(line.stages).map(
                  ([k, v]) => [k, fmtNum(v)] as [string, ReactNode],
                )}
              />
            </>
          )}

          <PanelTitle>Наши отчёты</PanelTitle>
          {data.report_items.length === 0 ? (
            <Muted style={{ marginBottom: 16 }}>Строка ещё не попадала в наши отчёты.</Muted>
          ) : (
            <TableWrap $maxHeight="30vh" style={{ marginBottom: 16 }}>
              <DataTable>
                <thead>
                  <tr>
                    <th>Документ</th>
                    <th>Дата</th>
                    <th>Статус</th>
                    <th className="num">Объём</th>
                  </tr>
                </thead>
                <tbody>
                  {data.report_items.map((ri) => (
                    <tr
                      key={ri.report_id}
                      data-click="true"
                      onClick={() => onOpenReport(ri.report_id)}
                    >
                      <td>{REPORT_KIND_LABEL[ri.kind] ?? ri.kind}</td>
                      <td className="nowrap">{fmtDate(ri.report_date)}</td>
                      <td>
                        <Tag $tone={ri.status === 'issued' ? 'success' : 'neutral'}>
                          {ri.status === 'issued' ? 'выпущен' : 'черновик'}
                        </Tag>
                      </td>
                      <td className="num">{fmtNum(ri.qty)}</td>
                    </tr>
                  ))}
                </tbody>
              </DataTable>
            </TableWrap>
          )}

          <PanelTitle>RFI по строке</PanelTitle>
          {data.rfi.length === 0 ? (
            <Muted style={{ marginBottom: 16 }}>Предъявлений нет.</Muted>
          ) : (
            <TableWrap $maxHeight="30vh" style={{ marginBottom: 16 }}>
              <DataTable>
                <thead>
                  <tr>
                    <th>Дата</th>
                    <th>№ RFI</th>
                    <th>№ RFI ПО</th>
                    <th>Контроль</th>
                    <th className="num">Объём</th>
                    <th>Источник</th>
                    <th>Статус</th>
                    <th>Связи (ИД / ГК)</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rfi.map((r) => (
                    <tr key={r.id} data-tone={r.excluded ? 'muted' : undefined}>
                      <td className="nowrap">{fmtDate(r.held_on)}</td>
                      <td>{r.rfi_no ?? ''}</td>
                      <td className="nowrap">{r.rfi_no_po}</td>
                      <td>{CONTROL_KIND_LABEL[r.control_kind] ?? r.control_kind}</td>
                      <td className="num">{fmtNum(r.qty)}</td>
                      <td>{SOURCE_LABEL[r.source] ?? r.source}</td>
                      <td>
                        {RFI_STATUS_LABEL[r.status] ?? r.status}
                        {r.excluded ? ' · исключён' : ''}
                      </td>
                      <td className="nowrap" style={{ fontSize: 12 }}>
                        {[
                          r.inspection_request_id ? 'Заявка СК' : null,
                          r.executive_act_id ? 'АОСР' : null,
                          r.geodesy_scheme_id ? 'Исп. схема' : null,
                        ]
                          .filter(Boolean)
                          .join(' · ') || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </DataTable>
            </TableWrap>
          )}

          <PanelTitle>Гарантийные обязательства</PanelTitle>
          {data.guarantees.length === 0 ? (
            <Muted style={{ marginBottom: 16 }}>ГО по строке нет.</Muted>
          ) : (
            <TableWrap $maxHeight="30vh" style={{ marginBottom: 16 }}>
              <DataTable>
                <thead>
                  <tr>
                    <th>№ письма</th>
                    <th>Дата</th>
                    <th>Срок</th>
                    <th className="num">Объём</th>
                    <th className="num">ИД принято</th>
                    <th className="num">Остаток</th>
                    <th className="num">Отставание, дн.</th>
                    <th>Закрыто</th>
                  </tr>
                </thead>
                <tbody>
                  {data.guarantees.map((g) => (
                    <tr key={g.id} data-tone={isGuaranteeOverdue(g) ? 'danger' : undefined}>
                      <td>{g.letter_no}</td>
                      <td className="nowrap">{fmtDate(g.letter_date)}</td>
                      <td className="nowrap">{fmtDate(g.due_date)}</td>
                      <td className="num">{fmtNum(g.qty)}</td>
                      <td className="num">{fmtNum(g.id_accepted)}</td>
                      <td className="num">{fmtNum(g.remainder)}</td>
                      <td className="num">{g.lag_days ?? ''}</td>
                      <td className="nowrap">{guaranteeClosedText(g)}</td>
                    </tr>
                  ))}
                </tbody>
              </DataTable>
            </TableWrap>
          )}

          <PanelTitle>Изменения между срезами</PanelTitle>
          {data.changes.length === 0 ? (
            <Muted>Числа учёта Заказчика по строке не менялись.</Muted>
          ) : (
            <TableWrap $maxHeight="30vh">
              <DataTable>
                <thead>
                  <tr>
                    <th>Когда</th>
                    <th>Поле</th>
                    <th className="num">Было</th>
                    <th className="num">Стало</th>
                  </tr>
                </thead>
                <tbody>
                  {data.changes.map((c, i) => (
                    <tr key={`${c.import_id}-${c.field}-${i}`}>
                      <td className="nowrap">{fmtDateTime(c.created_at)}</td>
                      <td>{fieldLabel(c.field)}</td>
                      <td className="num">{val(c.old_value)}</td>
                      <td className="num">{val(c.new_value)}</td>
                    </tr>
                  ))}
                </tbody>
              </DataTable>
            </TableWrap>
          )}
        </>
      )}
    </Drawer>
  );
}
